import { useState, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Check, ArrowDown, CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { getIconComponent } from "@/lib/statusIconsMap";
import { STATUS_COLORS } from "@/lib/colorsMap";

export function CompleteTaskDialog({ 
    open, 
    onOpenChange, 
    task, 
    onComplete,
    isLoading = false
}) {
    const [selectedStatus, setSelectedStatus] = useState(null);
    const [completedDate, setCompletedDate] = useState(new Date());
    const [calendarOpen, setCalendarOpen] = useState(false);

    // Get completion statuses from task's available statuses
    // Fall back to checking for 'done' status if no triggersComplete flags are set (legacy projects)
    const hasTriggersCompleteFlags = (task?.availableStatuses || []).some(s => s.triggersComplete !== undefined);
    
    const completionStatuses = hasTriggersCompleteFlags
        ? (task?.availableStatuses || []).filter(s => s.triggersComplete)
        : (task?.availableStatuses || []).filter(s => s.id === 'done');
    
    // Check if current status is already a completion status
    const currentStatusIsCompletion = completionStatuses.some(s => s.id === task?.status?.id);
    
    // Get current status config
    const currentStatus = (task?.availableStatuses || []).find(s => s.id === task?.status?.id);

    // Reset state when dialog opens - only run when open changes to true
    useEffect(() => {
        if (open) {
            // If already on a completion status, pre-select it
            const isCurrentCompletion = completionStatuses.some(s => s.id === task?.status?.id);
            if (isCurrentCompletion) {
                setSelectedStatus(task?.status?.id);
            } else if (completionStatuses.length === 1) {
                // Auto-select if only one completion status
                setSelectedStatus(completionStatuses[0].id);
            } else {
                setSelectedStatus(null);
            }
            // Default to today or existing completedAt
            setCompletedDate(task?.completedAt ? new Date(task.completedAt) : new Date());
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleComplete = () => {
        if (!selectedStatus && !currentStatusIsCompletion) return;
        if (!completedDate) return; // Guard against null/undefined date
        
        onComplete({
            completedAt: completedDate,
            // Only include status if we need to change it
            targetStatus: currentStatusIsCompletion ? null : selectedStatus
        });
    };

    // If no completion statuses configured, show a message
    if (open && completionStatuses.length === 0) {
        return (
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle>No Completion Status Configured</DialogTitle>
                        <DialogDescription>
                            There are no statuses configured as completion statuses in this project. 
                            Go to Project Settings → Statuses and enable "Completes task" on at least one status.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => onOpenChange(false)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px]">
                <DialogHeader>
                    <DialogTitle>
                        {task?.completedAt ? 'Update Completed Date' : 'Complete Task'}
                    </DialogTitle>
                    <DialogDescription>
                        {currentStatusIsCompletion 
                            ? 'Set or update when this task was completed.'
                            : 'Mark this task as complete and set the completion date.'}
                    </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                    {/* Only show status selection if not already on a completion status */}
                    {!currentStatusIsCompletion && (
                        <>
                            {/* Current Status */}
                            <div className="space-y-2">
                                <Label className="text-sm text-muted-foreground">Current status</Label>
                                <div className="flex items-center gap-3 p-3 border rounded-lg bg-muted/50">
                                    {(() => {
                                        const IconComponent = currentStatus ? getIconComponent(currentStatus.icon) : null;
                                        return (
                                            <>
                                                {IconComponent && (
                                                    <IconComponent className={`h-5 w-5 ${STATUS_COLORS[currentStatus?.color]?.text || 'text-gray-500'}`} />
                                                )}
                                                <span className="font-medium">{currentStatus?.name || task?.status?.name || 'Unknown'}</span>
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Arrow */}
                            <div className="flex justify-center">
                                <ArrowDown className="h-5 w-5 text-muted-foreground" />
                            </div>

                            {/* Target Status Selection */}
                            <div className="space-y-2">
                                <Label className="text-sm text-muted-foreground">Move to completion status</Label>
                                <div className="grid gap-2">
                                    {completionStatuses.map(status => {
                                        const IconComponent = getIconComponent(status.icon);
                                        const isSelected = selectedStatus === status.id;
                                        return (
                                            <button
                                                key={status.id}
                                                type="button"
                                                onClick={() => setSelectedStatus(status.id)}
                                                className={`flex items-center gap-3 p-3 border rounded-lg text-left transition-all hover:bg-accent/50 ${
                                                    isSelected ? 'border-primary bg-accent' : 'border-border'
                                                }`}
                                            >
                                                <IconComponent className={`h-5 w-5 ${STATUS_COLORS[status.color]?.text || 'text-gray-500'}`} />
                                                <span className="font-medium">{status.name}</span>
                                                {isSelected && (
                                                    <Check className="h-4 w-4 ml-auto text-primary" />
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}

                    {/* Completion Date */}
                    <div className="space-y-2">
                        <Label className="text-sm text-muted-foreground">Completed on</Label>
                        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    className={cn(
                                        "w-full justify-start text-left font-normal",
                                        !completedDate && "text-muted-foreground"
                                    )}
                                >
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {completedDate ? format(completedDate, "PPP") : "Pick a date"}
                                </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    selected={completedDate}
                                    onSelect={(date) => {
                                        setCompletedDate(date);
                                        setCalendarOpen(false);
                                    }}
                                    disabled={(date) => date > new Date()}
                                    initialFocus
                                />
                            </PopoverContent>
                        </Popover>
                    </div>
                </div>

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleComplete}
                        disabled={!selectedStatus && !currentStatusIsCompletion}
                        loading={isLoading}
                    >
                        {task?.completedAt ? 'Update' : 'Complete Task'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

